import type { ActionType } from '@/shared/components/ActionsDropdown'
import type { ScheduleModel, SettingsTemplateModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { FieldErrors } from 'react-hook-form'
import type { OptimizationGroupFormData } from '../model/types'

import React, { useMemo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { filter } from 'lodash/fp'
import {
	AlertTriangleIcon,
	CheckIcon,
	EllipsisVerticalIcon,
	InfoIcon,
	RotateCcwIcon,
} from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { useTemplateActions } from '@/features/settings-template-actions'

import {
	getOgStatus,
	getStatusBadgeVariant,
	useOptimizationIsReadyForChange,
} from '@/entities/optimization'
import { ProductGroupsPicker } from '@/entities/optimization-group'
import {
	OptimizationScheduleDate,
	useSchedulesQuery,
} from '@/entities/optimization-schedule'
import { useAccess } from '@/entities/session'
import { UsernameById } from '@/entities/user'
import { VideoTutorials } from '@/entities/video-tutorial'

import { OptimizationGroupActionsMenu } from '@/widgets/og-actions-dropdown'
import { BusinessSettingsFieldsCard } from '@/widgets/scenario-business'
import { StopListFieldsCard } from '@/widgets/scenario-stoplist'
import { StrategyFieldsCard } from '@/widgets/scenario-strategy'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import NavigationPrompt from '@/shared/components/NavigationPrompt'
import { TreeCheckbox } from '@/shared/components/TreePicker'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	DropdownMenu,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Field, FieldContent, FieldError } from '@/shared/components/ui/field'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { Tag } from '@/shared/components/ui/tag'
import { ApplyAction, SettingsTemplateType, VideoTag } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber } from '@/shared/tools/locale'

import {
	OptimizationGroupItemProvider,
	useOptimizationGroupItemContext,
} from './OptimizationGroupItemProvider'

export default function OptimizationGroupModalWrapped({
	isOpen,
	close,
	optimizationGroupId,
	initialTabId,
}: {
	isOpen: boolean
	close: () => void
	optimizationGroupId: string
	initialTabId?: 'structure' | 'strategy' | 'business' | 'stop_list'
}) {
	return (
		<OptimizationGroupItemProvider optimizationGroupId={optimizationGroupId}>
			<OptimizationGroupModal
				isOpen={isOpen}
				close={close}
				initialTabId={initialTabId}
			/>
		</OptimizationGroupItemProvider>
	)
}
const OptimizationGroupModal = ({
	isOpen,
	close,
	initialTabId,
}: {
	isOpen: boolean
	close: () => void
	initialTabId?: 'structure' | 'strategy' | 'business' | 'stop_list'
}) => {
	const { t } = useTranslation()

	const context = useOptimizationGroupItemContext()
	const handleOpenChange = async (open: boolean) => {
		if (context.methods.formState.isDirty && !open) {
			const answer = await confirm({
				title: t('navigation.blocked.pc_settings.title'),
				description: <Trans i18nKey='navigation.blocked.pc_settings.message' />,
				actionText: t('navigation.blocked.pc_settings.ok'),
				cancelText: t('navigation.blocked.pc_settings.cancel'),
			})
			if (answer) {
				close()
			}
		} else if (!open) {
			close()
		}
	}

	return (
		<Modal open={isOpen} onOpenChange={handleOpenChange}>
			<ModalContent className='h-[calc(100vh-2rem)] max-w-5xl p-0'>
				<ErrorBoundary>
					{context.isLoading ? (
						<Empty>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<Spinner className='text-primary' strokeWidth={6} />
								</EmptyMedia>
								<EmptyDescription>Loading...</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<OptimizationGroupForm initialTabId={initialTabId} />
					)}
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}

function OptimizationGroupForm({
	initialTabId,
}: {
	initialTabId?: 'structure' | 'strategy' | 'business' | 'stop_list'
}) {
	const { t } = useTranslation()

	const context = useOptimizationGroupItemContext()
	const { optimizationGroup, optimization } = context
	const scheduleQuery = useSchedulesQuery<ScheduleModel | undefined>({
		select: (schedules) =>
			optimizationGroup
				? schedules?.find((schedule) =>
						schedule.optimization_groups.includes(optimizationGroup.id)
					)
				: undefined,
	})
	const isReadyToChange = useOptimizationIsReadyForChange(optimizationGroup.id)
	const tabs = useMemo(() => getTabs(t), [t])

	return (
		<>
			<NavigationPrompt
				when={context.methods?.formState?.isDirty}
				title={t('navigation.blocked.pc_settings.title')}
				message={<Trans i18nKey='navigation.blocked.pc_settings.message' />}
				okText={t('navigation.blocked.pc_settings.ok')}
				cancelText={t('navigation.blocked.pc_settings.cancel')}
			/>
			<FormProvider
				methods={context.methods}
				onSubmit={context.handleSubmit}
				className='flex h-full flex-col overflow-hidden p-4'
			>
				<Blanket
					className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
					isTinted={context.isPending || context.methods.formState.isSubmitting}
				>
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<Spinner className='opacity-50' />
							</EmptyMedia>
							<EmptyDescription>
								{t('og.modal.pending_hint', {
									defaultValue: 'Oprimization group is saving...',
								})}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				</Blanket>
				<ModalHeader className='pr-6'>
					<div className='flex w-full items-start justify-between'>
						<ModalTitle data-testid='og-modal-title'>
							{context.methods.watch('name') || 'None'}
						</ModalTitle>
						<div className='flex items-center gap-2'>
							<VideoTutorials tags={[VideoTag.OptimizationGroupSettings]}>
								<Button className='ml-auto' variant='ghost' size='icon-sm'>
									<InfoIcon />
								</Button>
							</VideoTutorials>
							{optimizationGroup && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											data-testid='og-actions-trigger'
											variant='ghost'
											size='icon-sm'
										>
											<EllipsisVerticalIcon />
										</Button>
									</DropdownMenuTrigger>
									<OptimizationGroupActionsMenu
										optimizationGroupId={optimizationGroup.id}
									/>
								</DropdownMenu>
							)}
							<Separator orientation='vertical' className='mr-2 h-6!' />
						</div>
					</div>
				</ModalHeader>
				<DescriptionList className='my-4 flex items-start gap-10'>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('general_status', { defaultValue: 'Status' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{optimization ? (
								<Tag
									variant={getStatusBadgeVariant(
										getOgStatus(optimization.status)
									)}
								>
									{t(`opt_${getOgStatus(optimization.status)}`)}
								</Tag>
							) : (
								'-'
							)}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('og.items', { defaultValue: 'Items in OG' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{optimization ? (
								<span className='font-medium'>
									{formatNumber(optimization?.total_products || 0)}
								</span>
							) : (
								'-'
							)}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('general_schedule', { defaultValue: 'Schedule repricing' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{scheduleQuery.data ? (
								<OptimizationScheduleDate
									className='font-medium'
									schedule={scheduleQuery.data}
								/>
							) : (
								'-'
							)}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('last_modified_at', { defaultValue: 'Last modified' })}
						</DescriptionTerm>
						<DescriptionDetail>
							{optimizationGroup?.updated_at ? (
								<p className='flex items-center font-medium'>
									{formatDistanceToNow(
										new Date(optimizationGroup.updated_at + 'Z'),
										{ addSuffix: true }
									)}
									{optimizationGroup?.updated_by && ' by '}
									{optimizationGroup?.updated_by && (
										<UsernameById userId={optimizationGroup.updated_by} />
									)}
								</p>
							) : optimizationGroup?.created_at ? (
								<p className='flex items-center font-medium'>
									{formatDistanceToNow(
										new Date(optimizationGroup.created_at + 'Z'),
										{ addSuffix: true }
									)}
									{optimizationGroup?.created_by && ' by '}
									{optimizationGroup?.created_by && (
										<UsernameById userId={optimizationGroup.created_by} />
									)}
								</p>
							) : (
								'-'
							)}
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
				{!isReadyToChange && (
					<Alert className='mb-4' variant='neutral'>
						<InfoIcon />
						<AlertDescription>
							{t(
								`app.optimization.update_is_forbidden.${optimization?.status}`
							)}
						</AlertDescription>
					</Alert>
				)}
				<Tabs defaultValue={initialTabId || 'structure'} asChild>
					<ModalBody className='overflow-hidden'>
						<TabsList>
							{tabs.map((tab) => (
								<TabsTrigger
									data-testid='og-modal-tab'
									data-tab={tab.key}
									key={tab.key}
									value={tab.key}
									onClick={() => {
										ampli.settingsOgModalTabClick({
											tab: tab.key,
										})
									}}
								>
									{tab.label}
									{tab.hasError(context.methods.formState.errors) && (
										<AlertTriangleIcon className='text-destructive' />
									)}
								</TabsTrigger>
							))}
						</TabsList>
						{tabs.map(({ TabContent, key }) => (
							<TabsContent
								data-testid='og-modal-tab-content'
								className='-mx-4 flex flex-col overflow-y-auto px-4'
								value={key}
								key={key}
							>
								<TabContent />
							</TabsContent>
						))}
					</ModalBody>
				</Tabs>
				<ModalFooter className='pt-4'>
					<ModalClose asChild>
						<Button
							className='mr-auto'
							data-testid='og-modal-cancel'
							disabled={context.isPending}
						>
							{t('general_cancel')}
						</Button>
					</ModalClose>
					<div className='flex items-center gap-2'>
						{isReadyToChange && context.methods.formState.isDirty && (
							<div className='flex items-center space-x-2'>
								<Button
									disabled={context.isPending}
									data-testid='og-form-revert-changes'
									onClick={context.handleReset}
									variant='ghost'
								>
									<RotateCcwIcon />
									{t('app.revert_changes')}
								</Button>
								<Button
									disabled={context.isPending}
									data-testid='og-form-save-and-update'
									onClick={context.handleSubmitAndUpdate}
								>
									{t('app.save_and_update')}
								</Button>
								<Button
									variant='warning'
									data-testid='og-form-save'
									onClick={context.handleSubmit}
									disabled={context.isPending}
								>
									<CheckIcon />
									{t('general_save')}
								</Button>
							</div>
						)}
					</div>
				</ModalFooter>
			</FormProvider>
		</>
	)
}

const getTabs = (t: TFunction) =>
	[
		{
			key: 'structure',
			label: t('settings.structure.short', { defaultValue: 'Structure' }),
			hasError: (errors: FieldErrors<OptimizationGroupFormData>) =>
				errors?.sales_entities || errors?.product_groups,
			TabContent: StructureTab,
		},
		{
			key: 'strategy',
			label: t('settings.strategy.short', { defaultValue: 'Strategy' }),
			hasError: (errors: FieldErrors<OptimizationGroupFormData>) =>
				errors?.strategy,
			TabContent: StrategyTab,
		},

		{
			key: 'business',
			label: t('settings.business.short', { defaultValue: 'Constrains' }),
			hasError: (errors: FieldErrors<OptimizationGroupFormData>) =>
				errors?.business,
			TabContent: BusinessConstraintsTab,
		},
		{
			key: 'stop_list',
			label: t('settings.stop_list.short', { defaultValue: 'Stop list' }),
			hasError: (errors: FieldErrors<OptimizationGroupFormData>) =>
				errors?.stop_list,
			TabContent: StopListTab,
		},
	] as const

function StructureTab() {
	const { t } = useTranslation()

	const { isLoading, salesEntitiesTree, productGroups } =
		useOptimizationGroupItemContext()
	const { checkPermissons } = useAccess()
	const isDiabled = !checkPermissons(['MANAGE_OPTIMIZATION_GROUP'])
	if (isLoading) {
		return (
			<div className='flex size-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<>
			<Card data-testid='og-settings-sales-entities-panel'>
				<CardHeader>
					<CardTitle>{t('sales_entities')}</CardTitle>
					<CardDescription>
						{t('og_settings.general.sales_entities.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<Controller
							name='sales_entities'
							render={({ field, fieldState }) => (
								<Field>
									<FieldContent>
										<TreeCheckbox
											disabled={isDiabled}
											tree={salesEntitiesTree || []}
											value={field.value}
											onChange={field.onChange}
										/>
									</FieldContent>
									<FieldError
										errors={[{ message: fieldState.error?.message }]}
									/>
								</Field>
							)}
						/>
					</ErrorBoundary>
				</CardContent>
			</Card>
			<Card data-testid='og-settings-product-groups-panel' className='mt-3'>
				<CardHeader>
					<CardTitle>{t('product_groups')}</CardTitle>
					<CardDescription>
						{t('og_settings.general.product_groups.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ErrorBoundary>
						<Controller
							name='product_groups'
							render={({ field, fieldState }) => (
								<Field>
									<FieldContent>
										{productGroups ? (
											<ProductGroupsPicker
												data={productGroups}
												disabled={isDiabled}
												value={field.value}
												onChange={field.onChange}
											/>
										) : null}
									</FieldContent>
									<FieldError
										errors={[{ message: fieldState.error?.message }]}
									/>
								</Field>
							)}
						/>
					</ErrorBoundary>
				</CardContent>
			</Card>
		</>
	)
}
function StrategyTab() {
	const { optimizationGroup } = useOptimizationGroupItemContext()
	const { watch, setValue } = useFormContext()
	const settingName = 'strategy'
	const handleUseTemplate = ({ body }: SettingsTemplateModel) => {
		setValue(settingName, body[settingName as keyof typeof body], {
			shouldDirty: true,
		})
	}
	const templateActions = useTemplateActions({
		allowedTypes: [SettingsTemplateType.ScenarioStrategy],
		applyAction: ApplyAction.ReplaceMainScenario,
		templatePayload: {
			name: watch('name'),
			template_type: SettingsTemplateType.ScenarioStrategy,
			body: { strategy: watch(settingName) },
		},
		handleUseTemplate,
		optimizationGroupId: optimizationGroup.id,
	})

	return <StrategyFieldsCard actions={filterTemplateActions(templateActions)} />
}
function BusinessConstraintsTab() {
	const { optimizationGroup } = useOptimizationGroupItemContext()
	const { watch, setValue } = useFormContext()
	const settingName = 'business'
	const handleUseTemplate = ({ body }: SettingsTemplateModel) => {
		setValue(settingName, body[settingName as keyof typeof body], {
			shouldDirty: true,
		})
	}
	const templateActions = useTemplateActions({
		allowedTypes: [SettingsTemplateType.ScenarioBusinessConstraints],
		applyAction: ApplyAction.ReplaceMainScenario,
		templatePayload: {
			template_type: SettingsTemplateType.ScenarioBusinessConstraints,
			name: watch('name'),
			body: { [settingName]: watch(settingName) },
		},
		handleUseTemplate,
		optimizationGroupId: optimizationGroup.id,
	})

	return (
		<BusinessSettingsFieldsCard
			actions={filterTemplateActions(templateActions)}
		/>
	)
}
function StopListTab() {
	const { skuRules, optimizationGroup, mainScenario } =
		useOptimizationGroupItemContext()
	const { watch, setValue } = useFormContext()
	const settingName = 'stop_list'
	const handleUseTemplate = ({ body }: SettingsTemplateModel) => {
		setValue(settingName, body[settingName as keyof typeof body], {
			shouldDirty: true,
		})
	}
	const templateActions = useTemplateActions({
		allowedTypes: [SettingsTemplateType.ScenarioStopList],
		applyAction: ApplyAction.ReplaceMainScenario,
		templatePayload: {
			template_type: SettingsTemplateType.ScenarioStopList,
			name: watch('name'),
			body: { [settingName]: watch(settingName) },
		},
		optimizationGroupId: optimizationGroup.id,
		handleUseTemplate,
	})

	return (
		<ErrorBoundary>
			<StopListFieldsCard
				actions={filterTemplateActions(templateActions)}
				skuRules={skuRules}
				optimizationGroupId={optimizationGroup.id}
				scenarioId={mainScenario?.id}
			/>
		</ErrorBoundary>
	)
}
const filterTemplateActions = (actions: ActionType[]) =>
	filter(({ key }) => key !== 'apply_template', actions)
